/**
 * Sagas for the lexicons module (VE-24948 / U5).
 *
 * THIS FILE OWNS THE ADR-003 ORDERING, AND IT MUST NOT BE SPLIT UP.
 *
 * Every mutation goes through `performAuditedMutation`, which emits the audit
 * `intent`, performs the guarded write, then emits the `outcome`. That sequence
 * is what makes NFR-S4 (no audit, no mutation) structural rather than a policy
 * someone has to remember. If these handlers were spread across several sagas,
 * or if a component dispatched a write directly, the ordering guarantee would
 * degrade into a convention — and SEC-R4 records that this trail is the only
 * control distinguishing maintenance from destructive change under the flat
 * permission model (accepted risk R2).
 *
 * Watchers are exported individually and forked from `initMainPage`, matching
 * `redactionCodes` / `redactionStatuses`. They are NOT added to the app-startup
 * saga list: lexicon administration is a settings-route concern.
 *
 * The one exception is `onFetchLexicons`, which the document-editor route
 * forks on its own (`initDocumentEditor`). It answers ONE question — every live
 * lexicon in the org — for the settings table and the pickers alike. The trash
 * is `onFetchDeletedLexicons`, a separate watcher for a separate question, and
 * the settings page is the only thing that ever asks it. Neither reads a filter
 * out of the store; that is what left the editor fetching the trash after a
 * visit to Deleted Lexicons (VE-30783).
 */

import {
  call,
  cancelled,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'typed-redux-saga/macro';
import { modules } from '@veritone/glc-redux';
import { sagaIntl } from '@i18n';

import * as Actions from '../actions';
import { LEXICON_REGISTRY_ID_DEFAULT } from '../registry';
import {
  LexiconAuditBlockedError,
  LexiconAuditContext,
  LexiconAuditIncompleteError,
  LexiconAuditSnapshot,
  LexiconMutationKind,
  boundEntrySample,
  performAuditedMutation,
} from '../auditEmitter';
import {
  APPEND_CONFLICT_MAX_RETRIES,
  LEXICON_NAME_MAX_LENGTH,
} from '../limits';
import {
  Lexicon,
  LexiconData,
  LexiconEntryInput,
  LexiconSummary,
  liveEntries,
} from '../models';
import {
  LexiconNotFoundError,
  LexiconRevisionConflictError,
  LexiconServiceConfig,
  appendEntries,
  createLexicon,
  deleteLexicon,
  fetchAllDeletedLexicons,
  fetchAllLiveLexicons,
  fetchLexiconById,
  fetchLexiconSummaryById,
  isLexiconNameTaken,
  purgeEntries,
  removeEntries,
  renameLexicon,
  setLexiconFlags,
  updateEntries,
  updateLexiconGuarded,
} from '../services';
import { GraphQLExecutor, createGraphQLExecutor } from '../services/executor';
import { resolveLexiconSchemaId } from '../services/schemaResolver';
import { lexiconCsvFilename, lexiconToCsv } from '../csvExport';
import { downloadBlob } from '@redact-modules/casemedia/helper';
import { ensureRedactionCodesLoaded } from '@redact-modules/redactionCodes/sagas';
import { selectDraftDiff, selectEditing } from '../selectors';

const {
  config: { getConfig },
  user: { selectUser, selectUserOrganizationId },
} = modules;

/**
 * Everything the service and emitter need from the store, gathered once.
 *
 * The state snapshot technique mirrors `documents/sagas/exportPush.ts`:
 * `callGraphQLApi` wants a `getState`, and a saga has a value rather than a
 * live accessor. A snapshot is correct here because a single mutation's calls
 * all belong to one user action.
 */
function* getSagaContext() {
  const state = yield* select((s: unknown) => s);
  const exec = createGraphQLExecutor(() => state);
  const runtimeConfig = getConfig<Window['config']>(state as never);
  // `selectUser` has NO top-level `organizationId` — the org lives at
  // `organization.organizationId`, and the library ships a selector for it.
  // The previous code asserted `{ organizationId?: string }` onto the result,
  // which typechecked and read `undefined` at runtime, so every audit event in
  // every org was emitted with an empty index key (review finding 1).
  const user = selectUser(state as never) as { email?: string } | undefined;
  const organizationId = selectUserOrganizationId(state as never);

  // Config wins; the code default is the fallback. `window.config` is an
  // unreliable channel for a key the feature cannot work without — see
  // `registry.ts` — so an absent id is not treated as "this environment has no
  // lexicons", which is what left the page dead on a machine whose gitignored
  // `static/config.js` predated the key.
  const config: LexiconServiceConfig = {
    lexiconRegistryId:
      runtimeConfig.lexiconRegistryId || LEXICON_REGISTRY_ID_DEFAULT,
  };

  const auditContext: LexiconAuditContext = {
    // NFR-S6 — actor comes from the authenticated session, never a client field.
    actor: user?.email ?? '',
    organizationId: organizationId ?? '',
    now: () => new Date().toISOString(),
  };

  return { exec, config, auditContext };
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : 'Unexpected error';
}

/**
 * Reports a mutation failure, distinguishing the three cases that matter.
 *
 * A generic "something went wrong" would be actively harmful here: the user
 * needs to know whether their change was applied. Fail-closed means "not
 * applied"; an incomplete audit means "applied, but the record is incomplete";
 * a conflict means "not applied, and someone else changed it".
 */
function* reportMutationError(error: unknown) {
  if (error instanceof LexiconAuditBlockedError) {
    yield* put(
      Actions.LEXICON_MUTATION_BLOCKED_BY_AUDIT({ message: error.message })
    );
    return;
  }
  if (error instanceof LexiconAuditIncompleteError) {
    yield* put(
      Actions.LEXICON_MUTATION_AUDIT_INCOMPLETE({
        operationId: error.operationId,
        message: error.message,
      })
    );
    return;
  }
  if (error instanceof LexiconRevisionConflictError) {
    yield* put(
      Actions.SET_LEXICON_CONFLICT({
        modifiedBy: error.modifiedBy,
        modifiedDateTime: error.modifiedDateTime,
      })
    );
    return;
  }
  yield* put(Actions.LEXICON_MUTATION_FAILURE({ message: messageOf(error) }));
}

// ---------------------------------------------------------------------------
// Listing
// ---------------------------------------------------------------------------

/**
 * Settles the in-flight flag, once, from a mutation saga's `finally`.
 *
 * WHY A `finally` AND NOT ANOTHER `put` PER PATH. Every mutation action sets
 * `isMutating: true` in the reducer, and clearing it used to depend on the saga
 * reaching an outcome action. Two shapes defeated that: `getSagaContext` ran
 * OUTSIDE the `try`, so a failure to resolve the lexicon schema or the auth
 * context cleared nothing, and `onSaveLexiconDraft` had an early `return` that
 * dispatched nothing. Either one left the whole page's mutation controls
 * disabled until reload — including the CSV dialog's Import button, which is
 * gated on `isMutating` and belongs to a different flow entirely.
 *
 * The cancellation guard matters for `onSaveLexiconDraft`, the one watcher on
 * `takeLatest`: a superseded run must NOT clear the flag, because the run that
 * replaced it is still in flight and set it again. The replacement's own
 * `finally` is what settles it.
 */
function* settleMutation() {
  if (!(yield* cancelled())) {
    yield* put(Actions.LEXICON_MUTATION_SETTLED());
  }
}

/**
 * Resolves the registry, separating "this environment has no lexicons" from
 * "the call failed".
 *
 * The distinction is the whole of NFR-U5-12 and it is carried by the RETURN
 * SHAPE, not by an error type: a clean response with no published schema means
 * the feature is off here, and a throw means we do not know. Collapsing the two
 * would either hide the feature on a network blip or show a page-level error in
 * every environment that simply has not been provisioned yet.
 */
function* resolveRegistryAvailability(
  exec: GraphQLExecutor,
  lexiconRegistryId: string | undefined
) {
  const schemaId = yield* call(resolveLexiconSchemaId, exec, lexiconRegistryId);
  return schemaId !== null;
}

/**
 * One-shot availability check, forked at app start.
 *
 * WHY EAGER, WHEN EVERY CONSUMER ALSO FETCHES. The side-nav entry has to decide
 * whether to render BEFORE anyone visits the Lexicons page, and it is the one
 * consumer that cannot fetch on its own behalf — a nav item that fetches to
 * decide whether to exist would run on every route. So the availability of the
 * registry is established once, here, and every surface reads the same
 * `isNotConfigured` flag. `resolveLexiconSchemaId` caches, so the page's own
 * fetch moments later costs no second round trip.
 *
 * A THROW IS DELIBERATELY IGNORED. This probe only ever turns the feature OFF.
 * If the call fails we do not know, so we leave the flag alone and let the
 * first real fetch report a retryable failure — the nav entry showing for a
 * feature that turns out to be unavailable is a far smaller cost than the
 * feature vanishing because one request timed out at boot.
 */
export function* probeLexiconAvailability() {
  const { exec, config } = yield* call(getSagaContext);
  try {
    const available = yield* call(
      resolveRegistryAvailability,
      exec,
      config.lexiconRegistryId
    );
    if (!available) {
      yield* put(Actions.LEXICONS_NOT_CONFIGURED());
    }
  } catch {
    // Unknown, not unavailable. See above.
  }
}

/**
 * THE LIBRARY: every live lexicon in the org.
 *
 * One question, one answer, for every caller — the settings table, the editor's
 * Lexicon button, the upload selector. It reads nothing out of the store, which
 * is the whole of the VE-30783 fix: the scope used to come from `showDeleted` /
 * `offset` / `limit`, state the settings page owns and that outlives it, so a
 * picker opening after a visit to Deleted Lexicons asked for the trash.
 */
export function* onFetchLexicons() {
  yield* takeLatest(Actions.FETCH_LEXICONS, function* () {
    const { exec, config } = yield* call(getSagaContext);
    try {
      const available = yield* call(
        resolveRegistryAvailability,
        exec,
        config.lexiconRegistryId
      );
      if (!available) {
        // NFR-U5-12 — an environment without a provisioned registry is a
        // legitimate state, not an error. Hide the feature; say nothing.
        yield* put(Actions.LEXICONS_NOT_CONFIGURED());
        return;
      }
      const lexicons = yield* call(fetchAllLiveLexicons, exec, config);
      yield* put(Actions.FETCH_LEXICONS_SUCCESS({ lexicons }));
    } catch (error) {
      yield* put(Actions.FETCH_LEXICONS_FAILURE({ message: messageOf(error) }));
    }
  });
}

/**
 * THE TRASH: every soft-deleted lexicon in the org.
 *
 * Its own watcher so that asking for it is a deliberate act. The settings page
 * dispatches this beside the library fetch — it needs the rows for the deleted
 * view and their count for the link that leads there — and nothing else
 * dispatches it at all, so the editor never carries the trash over the wire.
 */
export function* onFetchDeletedLexicons() {
  yield* takeLatest(Actions.FETCH_DELETED_LEXICONS, function* () {
    const { exec, config } = yield* call(getSagaContext);
    try {
      const available = yield* call(
        resolveRegistryAvailability,
        exec,
        config.lexiconRegistryId
      );
      if (!available) {
        yield* put(Actions.LEXICONS_NOT_CONFIGURED());
        return;
      }
      const lexicons = yield* call(fetchAllDeletedLexicons, exec, config);
      yield* put(Actions.FETCH_DELETED_LEXICONS_SUCCESS({ lexicons }));
    } catch (error) {
      yield* put(
        Actions.FETCH_DELETED_LEXICONS_FAILURE({ message: messageOf(error) })
      );
    }
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/** Generates the ADR-003 idempotency key: one per user action. */
function newOperationId(): string {
  // Deliberately not reused across user actions. Retries of the SAME action
  // reuse their id (see the append retry loop below) so a reviewer can collapse
  // duplicates; a new action must look like a new operation.
  return `op-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * BR-1, checked before any write. The registry schema carries the same
 * `maxLength`, but its rejection reaches the user as "The provided JSON had
 * validation errors" with no field named (VE-29492). Callers pass the TRIMMED
 * name — the value that would be written.
 *
 * Goes through `sagaIntl` rather than an English literal so the drawer's copy
 * and this one cannot drift: both resolve the SAME `lexiconNameTooLong` key,
 * and a French user who reaches this path reads French. The neighbouring
 * "required" / "taken" literals predate this and are still untranslated —
 * see `plans/lexicons-follow-ups.md`.
 */
function* rejectNameTooLong(name: string) {
  if (name.length <= LEXICON_NAME_MAX_LENGTH) {
    return false;
  }
  const intl = sagaIntl();
  yield* put(
    Actions.SET_DRAFT_NAME_ERROR({
      message: intl.formatMessage(
        {
          id: 'lexiconNameTooLong',
          defaultMessage: 'A lexicon name must be at most {max} characters.',
        },
        { max: LEXICON_NAME_MAX_LENGTH }
      ),
    })
  );
  return true;
}

export function* onCreateLexicon() {
  yield* takeEvery(Actions.CREATE_LEXICON, function* ({ payload }) {
    const name = payload.name.trim();
    try {
      if (yield* rejectNameTooLong(name)) {
        return;
      }
      // INSIDE the try on purpose: resolving the registry schema or the auth
      // context can fail, and when this sat outside it that failure cleared
      // nothing and reported nothing — the page's mutation controls simply
      // stopped working until reload.
      const { exec, config, auditContext } = yield* call(getSagaContext);
      // BR-2 — names are unique per org, compared case-insensitively. The
      // platform does not enforce SDO-field uniqueness, so this pre-write check
      // IS the enforcement; the inline error beats a platform rejection to
      // decode. (Two simultaneous creates can still race past it — accepted:
      // the loser is a duplicate NAME, not lost data, and rename fixes it.)
      const taken = yield* call(isLexiconNameTaken, exec, config, name);
      if (taken) {
        yield* put(
          Actions.SET_DRAFT_NAME_ERROR({
            message: 'A lexicon with this name already exists.',
          })
        );
        return;
      }
      const created = yield* call(
        performAuditedLexiconCreate,
        exec,
        config,
        auditContext,
        name
      );
      yield* put(Actions.LEXICON_MUTATION_SUCCESS({ lexicon: created }));
      yield* put(Actions.CLOSE_LEXICON_DRAWER());
      yield* put(Actions.FETCH_LEXICONS());
    } catch (error) {
      yield* reportMutationError(error);
    } finally {
      yield* settleMutation();
    }
  });
}

/**
 * Create is the one mutation whose lexicon id does not exist beforehand.
 *
 * The intent event therefore carries a placeholder id. That is a real wart, and
 * the alternative — creating first and auditing after — is the audit-first-vs-
 * write-first trade ADR-003 rejected. The `operationId` is what actually links
 * the pair, so the placeholder costs nothing to a reader using
 * `pairLexiconAuditEvents`.
 */
function performAuditedLexiconCreate(
  exec: GraphQLExecutor,
  config: LexiconServiceConfig,
  auditContext: LexiconAuditContext,
  name: string,
  entries: readonly LexiconEntryInput[] = []
): Promise<Lexicon> {
  const sample = boundEntrySample(
    entries.map((e, i) => ({ entryId: `new-${i}`, ...e }))
  );
  return performAuditedMutation(
    exec,
    auditContext,
    {
      operationId: newOperationId(),
      kind: 'lexicon.create',
      lexiconId: 'pending',
      lexiconName: name,
      after: {
        name,
        entryCount: entries.length,
        ...(entries.length > 0 ? { entries: sample.entries } : {}),
        ...(sample.entriesTruncated !== undefined
          ? { entriesTruncated: sample.entriesTruncated }
          : {}),
      },
    },
    async () => {
      const created = await createLexicon(exec, config, {
        name,
        entries,
        actor: auditContext.actor,
        now: auditContext.now(),
      });
      return { result: created, revisionTo: created.revision };
    }
  );
}

export function* onRenameLexicon() {
  yield* takeEvery(Actions.RENAME_LEXICON, function* ({ payload }) {
    try {
      if (yield* rejectNameTooLong(payload.name.trim())) {
        return;
      }
      // INSIDE the try on purpose: resolving the registry schema or the auth
      // context can fail, and when this sat outside it that failure cleared
      // nothing and reported nothing — the page's mutation controls simply
      // stopped working until reload.
      const { exec, config, auditContext } = yield* call(getSagaContext);
      // BR-2, same pre-write check as create — excluding the lexicon being
      // renamed, so renaming to its own name (a case tweak) is legal.
      const taken = yield* call(
        isLexiconNameTaken,
        exec,
        config,
        payload.name.trim(),
        payload.lexiconId
      );
      if (taken) {
        yield* put(
          Actions.SET_DRAFT_NAME_ERROR({
            message: 'A lexicon with this name already exists.',
          })
        );
        return;
      }
      const current = yield* call(
        fetchLexiconById,
        exec,
        config,
        payload.lexiconId
      );
      const updated = yield* call(runGuardedMutation, {
        exec,
        config,
        auditContext,
        kind: 'lexicon.rename' as const,
        current,
        detail: { newName: payload.name.trim() },
        before: { name: current.name },
        after: { name: payload.name.trim() },
        mutate: (c: Lexicon) => renameLexicon(c, payload.name),
      });
      yield* put(Actions.LEXICON_MUTATION_SUCCESS({ lexicon: updated }));
      yield* put(Actions.FETCH_LEXICONS());
    } catch (error) {
      yield* reportMutationError(error);
    } finally {
      yield* settleMutation();
    }
  });
}

export function* onAddLexiconEntries() {
  yield* takeEvery(Actions.ADD_LEXICON_ENTRIES, function* ({ payload }) {
    try {
      // INSIDE the try on purpose: resolving the registry schema or the auth
      // context can fail, and when this sat outside it that failure cleared
      // nothing and reported nothing — the page's mutation controls simply
      // stopped working until reload.
      const { exec, config, auditContext } = yield* call(getSagaContext);
      const updated = yield* call(
        addEntriesWithRetry,
        exec,
        config,
        auditContext,
        payload.lexiconId,
        payload.entries
      );
      yield* put(Actions.LEXICON_MUTATION_SUCCESS({ lexicon: updated }));
      yield* put(Actions.CANCEL_CSV_IMPORT());
      yield* put(Actions.FETCH_LEXICONS());
    } catch (error) {
      yield* reportMutationError(error);
    } finally {
      yield* settleMutation();
    }
  });
}

/**
 * Adds entries, retrying on a revision conflict (Q6 / BR-6).
 *
 * Appends are COMMUTATIVE — U5-FR13 says a further CSV *adds* terms — so two
 * concurrent imports should both land, and re-reading then re-appending is
 * safe and invisible. Renames and deletions are not commutative and go to the
 * user instead.
 *
 * Each attempt gets its own `operationId`, so the trail shows the retry
 * honestly rather than hiding it. Capped so a hot lexicon cannot spin.
 *
 * Exported so the retry cap and the commutativity assumption are directly
 * testable — the cap is the kind of thing that silently becomes infinite.
 */
export async function addEntriesWithRetry(
  exec: GraphQLExecutor,
  config: LexiconServiceConfig,
  auditContext: LexiconAuditContext,
  lexiconId: string,
  entries: readonly LexiconEntryInput[]
): Promise<Lexicon> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= APPEND_CONFLICT_MAX_RETRIES; attempt += 1) {
    const current = await fetchLexiconById(exec, config, lexiconId);
    try {
      const sample = boundEntrySample(
        entries.map((e, i) => ({ entryId: `new-${i}`, ...e }))
      );
      return await performAuditedMutation(
        exec,
        auditContext,
        {
          operationId: newOperationId(),
          kind: 'lexicon.entries.add',
          lexiconId,
          lexiconName: current.name,
          revisionFrom: current.revision,
          detail: { entryCount: entries.length },
          before: { entryCount: current.entries.length },
          after: {
            entryCount: current.entries.length + entries.length,
            entries: sample.entries,
            ...(sample.entriesTruncated !== undefined
              ? { entriesTruncated: sample.entriesTruncated }
              : {}),
          },
        },
        async () => {
          const written = await updateLexiconGuarded(exec, config, {
            lexiconId,
            baseRevision: current.revision,
            actor: auditContext.actor,
            now: auditContext.now(),
            mutate: (c) => appendEntries(c, entries),
          });
          return { result: written.lexicon, revisionTo: written.revisionTo };
        }
      );
    } catch (error) {
      if (!(error instanceof LexiconRevisionConflictError)) {
        throw error;
      }
      lastError = error;
    }
  }
  throw lastError;
}

export function* onRemoveLexiconEntries() {
  yield* takeEvery(Actions.REMOVE_LEXICON_ENTRIES, function* ({ payload }) {
    try {
      // INSIDE the try on purpose: resolving the registry schema or the auth
      // context can fail, and when this sat outside it that failure cleared
      // nothing and reported nothing — the page's mutation controls simply
      // stopped working until reload.
      const { exec, config, auditContext } = yield* call(getSagaContext);
      const current = yield* call(
        fetchLexiconById,
        exec,
        config,
        payload.lexiconId
      );
      const removing = new Set(payload.entryIds);
      const removed = current.entries.filter((e) => removing.has(e.entryId));
      const sample = boundEntrySample(removed);
      const updated = yield* call(runGuardedMutation, {
        exec,
        config,
        auditContext,
        kind: 'lexicon.entries.remove' as const,
        current,
        detail: { entryCount: payload.entryIds.length },
        before: {
          entryCount: current.entries.length,
          entries: sample.entries,
          ...(sample.entriesTruncated !== undefined
            ? { entriesTruncated: sample.entriesTruncated }
            : {}),
        },
        after: { entryCount: current.entries.length - removed.length },
        mutate: (c: Lexicon) => removeEntries(c, payload.entryIds),
      });
      yield* put(Actions.LEXICON_MUTATION_SUCCESS({ lexicon: updated }));
      yield* put(Actions.FETCH_LEXICONS());
    } catch (error) {
      yield* reportMutationError(error);
    } finally {
      yield* settleMutation();
    }
  });
}

/**
 * The settings table's Enabled / Auto Apply checkboxes (VE-24948 / U5).
 *
 * The ONE mutation here that does not read a lexicon's entries, and the one
 * that does not move its revision — see `setLexiconFlags` for why both are
 * deliberate. It still goes through `performAuditedMutation` like everything
 * else in this file: turning a lexicon off is the change that stops it finding
 * anything without leaving any other trace, which makes it the one most worth
 * having in the trail, not the one to except from it (ADR-003).
 *
 * The reducer has already moved the checkbox by the time this runs. So a
 * failure re-fetches the page rather than only reporting itself: the notice
 * says what happened, and the fetch is what puts the tick back where the
 * registry actually has it.
 */
export function* onSetLexiconFlags() {
  yield* takeEvery(Actions.SET_LEXICON_FLAGS, function* ({ payload }) {
    try {
      const { exec, config, auditContext } = yield* call(getSagaContext);
      const current = yield* call(
        fetchLexiconSummaryById,
        exec,
        config,
        payload.lexiconId
      );
      yield* call(
        performAuditedMutation,
        exec,
        auditContext,
        {
          operationId: newOperationId(),
          kind: 'lexicon.settings' as LexiconMutationKind,
          lexiconId: payload.lexiconId,
          lexiconName: current.name,
          revisionFrom: current.revision,
          before: {
            name: current.name,
            enabled: current.enabled !== false,
            autoApply: current.autoApply === true,
          },
          after: {
            name: current.name,
            enabled: (payload.flags.enabled ?? current.enabled) !== false,
            autoApply: (payload.flags.autoApply ?? current.autoApply) === true,
          },
        },
        async () => {
          const written = await setLexiconFlags(exec, config, {
            lexiconId: payload.lexiconId,
            flags: payload.flags,
            actor: auditContext.actor,
            now: auditContext.now(),
          });
          // The revision does not move, so `revisionTo` is `revisionFrom` — and
          // saying so is better than omitting it, which would read as a write
          // whose result nobody recorded.
          return { result: written, revisionTo: written.revision };
        }
      );
      // Not for the checkbox, which moved already — for `modifiedDateTime` and
      // `modifiedBy`, which the write stamped and only the registry knows.
      yield* put(Actions.FETCH_LEXICONS());
    } catch (error) {
      yield* reportMutationError(error);
      // Puts the optimistic tick back where the registry actually has it.
      yield* put(Actions.FETCH_LEXICONS());
    } finally {
      yield* settleMutation();
    }
  });
}

/**
 * The table's Delete — a SOFT delete (BR-5, as amended).
 *
 * It raises `isDeleted` on the summary and the row moves to the deleted view.
 * The entries record is not read and not written: the flag is the whole change,
 * so deleting a 5,000-entry lexicon costs one read and one write.
 *
 * The audit kind stays `lexicon.delete`, because from every side that reads the
 * trail this IS the delete — the lexicon stops being offered, stops matching,
 * and stops holding its name. `lexicon.purge` is the separate event for the
 * separate act.
 *
 * Documents already redacted with it are unaffected either way: their
 * detections carry snapshots (U5-FR2).
 */
export function* onDeleteLexicon() {
  yield* takeEvery(Actions.DELETE_LEXICON, function* ({ payload }) {
    try {
      // INSIDE the try on purpose: resolving the registry schema or the auth
      // context can fail, and when this sat outside it that failure cleared
      // nothing and reported nothing — the page's mutation controls simply
      // stopped working until reload.
      const { exec, config, auditContext } = yield* call(getSagaContext);
      const current = yield* call(
        fetchLexiconSummaryById,
        exec,
        config,
        payload.lexiconId
      );
      // The instantiation expression pins `performAuditedMutation`'s generic:
      // `call` cannot infer it through the inline write callback, and the
      // result is the summary the row's new slot is chosen from.
      const written = yield* call(
        performAuditedMutation<LexiconSummary>,
        exec,
        auditContext,
        {
          operationId: newOperationId(),
          kind: 'lexicon.delete' as LexiconMutationKind,
          lexiconId: payload.lexiconId,
          lexiconName: current.name,
          revisionFrom: current.revision,
          before: {
            name: current.name,
            // From the summary's cache, not from counting an array this no
            // longer reads. It is the number the trail should record anyway —
            // how big the lexicon was when someone removed it.
            entryCount: current.entryCount,
          },
        },
        async () => {
          const written = await setLexiconFlags(exec, config, {
            lexiconId: payload.lexiconId,
            flags: { isDeleted: true },
            actor: auditContext.actor,
            now: auditContext.now(),
          });
          return { result: written, revisionTo: written.revision };
        }
      );
      // NOT `LEXICON_DELETED` — the record is still there, with a flag raised.
      // Dropping it from the store would empty the deleted view of the row
      // that was just put in it.
      //
      // THE ROW MOVES HERE, off the flag the registry just wrote, before
      // either refetch. Leaving the move to the refetches made a row's list
      // depend on two independent requests both landing (see the action).
      yield* put(Actions.LEXICON_SOFT_DELETE_CHANGED({ lexicon: written }));
      // BOTH LISTS, because a soft delete moves a row ACROSS them. These
      // reconcile what the line above already did — `modifiedDateTime` and
      // `modifiedBy` are stamped by the write and only the registry knows
      // them, and another admin may have changed either list meanwhile.
      yield* put(Actions.FETCH_LEXICONS());
      yield* put(Actions.FETCH_DELETED_LEXICONS());
    } catch (error) {
      yield* reportMutationError(error);
      // Puts both lists back where the registry actually has them, as the
      // flags mutation does. A write that SUCCEEDED and then failed to emit
      // its outcome throws here (`LexiconAuditIncompleteError`), so without
      // this the row stays live in the store while the registry has it
      // deleted — and the pickers keep offering it.
      yield* put(Actions.FETCH_LEXICONS());
      yield* put(Actions.FETCH_DELETED_LEXICONS());
    } finally {
      yield* settleMutation();
    }
  });
}

/**
 * Undo of a soft delete.
 *
 * THE NAME CHECK IS THE REASON THIS IS NOT JUST A FLAG WRITE. Deleting frees
 * the name (see `isLexiconNameTaken`), so between the delete and the restore
 * another lexicon may have taken it — and two live lexicons with one name are
 * indistinguishable in every picker, in the Categories dropdown, and in the
 * audit trail, where `lexiconName` is the only lexicon identifier NFR-S7
 * permits. The table does NOT disable Restore — see `DeletedRowActions` — so
 * this check is not a backstop behind a greyed-out button, it is the only gate
 * there is. It has to be: the store is a snapshot of a registry other clerks
 * are writing to, so a name taken since the last fetch is invisible to the
 * page and visible here.
 */
export function* onRestoreLexicon() {
  yield* takeEvery(Actions.RESTORE_LEXICON, function* ({ payload }) {
    try {
      const { exec, config, auditContext } = yield* call(getSagaContext);
      const current = yield* call(
        fetchLexiconSummaryById,
        exec,
        config,
        payload.lexiconId
      );
      const taken = yield* call(
        isLexiconNameTaken,
        exec,
        config,
        current.name,
        payload.lexiconId
      );
      if (taken) {
        yield* put(
          Actions.LEXICON_MUTATION_FAILURE({
            message: `A lexicon named "${current.name}" already exists. Rename it before restoring this one.`,
          })
        );
        return;
      }
      const written = yield* call(
        performAuditedMutation<LexiconSummary>,
        exec,
        auditContext,
        {
          operationId: newOperationId(),
          kind: 'lexicon.restore' as LexiconMutationKind,
          lexiconId: payload.lexiconId,
          lexiconName: current.name,
          revisionFrom: current.revision,
          after: { name: current.name, entryCount: current.entryCount },
        },
        async () => {
          const written = await setLexiconFlags(exec, config, {
            lexiconId: payload.lexiconId,
            flags: { isDeleted: false },
            actor: auditContext.actor,
            now: auditContext.now(),
          });
          return { result: written, revisionTo: written.revision };
        }
      );
      // The row crosses back, off the written flag, before either refetch.
      // This is the direction that mattered most: a restore whose deleted
      // refetch failed used to leave the row rendered in the TRASH as well as
      // the library, offering Permanently delete on a live lexicon.
      yield* put(Actions.LEXICON_SOFT_DELETE_CHANGED({ lexicon: written }));
      // BOTH LISTS: a restore is the soft delete run backwards. Reconciliation,
      // as above.
      yield* put(Actions.FETCH_LEXICONS());
      yield* put(Actions.FETCH_DELETED_LEXICONS());
    } catch (error) {
      yield* reportMutationError(error);
      // As in the soft delete: a write that landed and then failed to emit its
      // outcome must not leave the store disagreeing with the registry.
      yield* put(Actions.FETCH_LEXICONS());
      yield* put(Actions.FETCH_DELETED_LEXICONS());
    } finally {
      yield* settleMutation();
    }
  });
}

/**
 * PERMANENT delete, from the deleted view — the one irreversible operation.
 *
 * Both records go, summary first (see `deleteLexicon`). Its audit event says
 * "Permanently deleted" rather than "Deleted", because a reviewer reading the
 * trail years later has only that sentence to tell the recoverable act from
 * this one.
 */
export function* onPurgeLexicon() {
  yield* takeEvery(Actions.PURGE_LEXICON, function* ({ payload }) {
    try {
      const { exec, config, auditContext } = yield* call(getSagaContext);
      const current = yield* call(
        fetchLexiconSummaryById,
        exec,
        config,
        payload.lexiconId
      );
      yield* call(
        performAuditedMutation,
        exec,
        auditContext,
        {
          operationId: newOperationId(),
          kind: 'lexicon.purge' as LexiconMutationKind,
          lexiconId: payload.lexiconId,
          lexiconName: current.name,
          revisionFrom: current.revision,
          before: { name: current.name, entryCount: current.entryCount },
        },
        async () => {
          await deleteLexicon(exec, config, payload.lexiconId);
          return { result: undefined };
        }
      );
      yield* put(Actions.LEXICON_DELETED({ lexiconId: payload.lexiconId }));
      // The TRASH only. A purge destroys a row that was already out of the
      // library, so re-reading the library would be a request for rows that
      // cannot have changed.
      yield* put(Actions.FETCH_DELETED_LEXICONS());
    } catch (error) {
      yield* reportMutationError(error);
      // The same reason as the soft delete's: a purge that destroyed both
      // records and then failed to emit its outcome throws here, so
      // `LEXICON_DELETED` above never ran and the row is still in the trash.
      // The user clicks Permanently delete again and gets a not-found.
      yield* put(Actions.FETCH_DELETED_LEXICONS());
    } finally {
      yield* settleMutation();
    }
  });
}

/** Shared shape for the non-append guarded mutations. */
interface GuardedMutationArgs {
  readonly exec: GraphQLExecutor;
  readonly config: LexiconServiceConfig;
  readonly auditContext: LexiconAuditContext;
  readonly kind: LexiconMutationKind;
  readonly current: Lexicon;
  /**
   * The revision the caller's EDIT is based on — for the drawer, the one read
   * when it opened (BR-6). When absent, falls back to `current.revision`,
   * which only detects a race inside this call's own fetch→write window.
   * The drawer save MUST pass it: guarding on the fresh fetch made two admins'
   * edits silently last-write-wins, the exact case the conflict banner exists
   * for (review-gate finding 3).
   */
  readonly baseRevision?: number;
  readonly detail?: { readonly newName?: string; readonly entryCount?: number };
  readonly before?: LexiconAuditSnapshot;
  readonly after?: LexiconAuditSnapshot;
  readonly mutate: (current: Lexicon) => LexiconData;
}

function runGuardedMutation(args: GuardedMutationArgs): Promise<Lexicon> {
  const { exec, config, auditContext } = args;
  const baseRevision = args.baseRevision ?? args.current.revision;
  return performAuditedMutation(
    exec,
    auditContext,
    {
      operationId: newOperationId(),
      kind: args.kind,
      lexiconId: args.current.id,
      lexiconName: args.current.name,
      revisionFrom: baseRevision,
      ...(args.detail ? { detail: args.detail } : {}),
      ...(args.before ? { before: args.before } : {}),
      ...(args.after ? { after: args.after } : {}),
    },
    async () => {
      const written = await updateLexiconGuarded(exec, config, {
        lexiconId: args.current.id,
        baseRevision,
        actor: auditContext.actor,
        now: auditContext.now(),
        mutate: args.mutate,
      });
      return { result: written.lexicon, revisionTo: written.revisionTo };
    }
  );
}

/**
 * Loads specific lexicons by id, for the document apply path (VE-24948 / U5).
 *
 * Lives HERE rather than in the documents module because transport is this
 * module's business: the `GraphQLExecutor`, the registry-id resolution and the
 * SDO shape are all internal details, and the barrel deliberately does not
 * export them. The documents saga gets one call and a `Lexicon[]`.
 *
 * It cannot read the store instead. On the document-editor route the store does
 * hold the library (`FETCH_LEXICONS`), but that list is SUMMARIES — names,
 * counts, flags, no entries. The apply needs the entries, and it needs them for
 * the ids it was given, not for whatever the store last fetched.
 *
 * A lexicon that cannot be loaded is SKIPPED, not thrown on: one deleted or
 * unreadable lexicon must not void an apply of the others (the same principle
 * as BR-47 for a failing entry). Callers compare the returned ids against what
 * they asked for when they need to report the difference.
 */
export interface LexiconLoadResult {
  readonly lexicons: Lexicon[];
  /**
   * Requested ids the registry says do not exist — deleted between selection
   * and now. Nothing will ever load them, so the caller should resolve their
   * records rather than retry.
   */
  readonly missingIds: string[];
  /**
   * Requested ids whose fetch failed for ANY OTHER reason — transport, auth,
   * an unresolved schema. RETRYABLE, and the distinction is the whole point of
   * this type: treating one of these as "the lexicon has no matches" turns a
   * transient failure into a permanent, silent under-redaction.
   */
  readonly unreachableIds: string[];
}

/**
 * Ids of the lexicons an admin has marked AUTO APPLY (VE-24948 / U5).
 *
 * Lives here for the same reason `loadLexiconsByIds` does: transport is this
 * module's business, and the documents saga that calls it has no business
 * knowing about `GraphQLExecutor`, registry ids or the SDO shape. It gets ids.
 *
 * Reads the live list, so `enabled: false`, `isDeleted: true` and an empty
 * lexicon are all excluded before the flag is even consulted — the filter is
 * the same one the pickers use, which is what stops auto-apply from being a
 * back door into a lexicon nobody can choose by hand.
 *
 * Returns EMPTY on any failure rather than throwing. The caller runs on
 * document open, unasked; a registry blip must leave the document exactly as it
 * would have been, not surface an error the reviewer did not provoke.
 */
export function* loadAutoApplyLexiconIds() {
  try {
    const { exec, config } = yield* getSagaContext();
    const live = yield* call(fetchAllLiveLexicons, exec, config);
    return live
      .filter(
        (l) => l.autoApply === true && l.enabled !== false && l.entryCount > 0
      )
      .map((l) => l.id);
  } catch {
    return [];
  }
}

export function* loadLexiconsByIds(lexiconIds: readonly string[]) {
  const unique = [...new Set(lexiconIds)];
  if (unique.length === 0) {
    return {
      lexicons: [] as Lexicon[],
      missingIds: [] as string[],
      unreachableIds: [] as string[],
    };
  }
  const { exec, config } = yield* getSagaContext();
  const lexicons: Lexicon[] = [];
  const missingIds: string[] = [];
  const unreachableIds: string[] = [];
  for (const lexiconId of unique) {
    try {
      const lexicon = yield* call(fetchLexiconById, exec, config, lexiconId);
      lexicons.push(lexicon);
    } catch (err) {
      // No logging, by rule: an id is all we could safely emit anyway, and
      // terms must never reach a log (SEC-R3 / NFR-S2). But the gap is RETURNED
      // instead of swallowed — the previous version dropped it on the floor
      // while its comment claimed "the caller reports the gap", and the caller
      // could not, because it was never told. A lexicon that failed to fetch
      // then arrived at the apply as "0 matches", indistinguishable from a
      // lexicon that genuinely matched nothing.
      if (err instanceof LexiconNotFoundError) {
        missingIds.push(lexiconId);
      } else {
        unreachableIds.push(lexiconId);
      }
    }
  }
  return { lexicons, missingIds, unreachableIds };
}

/**
 * Commits the editor draft in ONE user action (VE-24948 / U5).
 *
 * The dialog composes entries locally and the user presses Create / Save —
 * the design prototype's model. Underneath, each kind of change is still its
 * own audited mutation (ADR-003): create carries the initial entries in one
 * write; an edit runs rename → append → remove sequentially, each an
 * intent→outcome pair. The non-commutative steps (rename, remove) are guarded
 * against the revision read WHEN THE DRAWER OPENED (`editing.baseRevision`),
 * advanced only by this save's own writes — that is what makes BR-6's
 * conflict banner actually fire when another admin changed the lexicon while
 * the drawer was open (review-gate finding 3). Appends auto-retry from a
 * fresh read because they are commutative (Q6 / U5-FR13). A failure part-way
 * leaves the dialog OPEN with the draft intact and the earlier steps already
 * applied and audited — nothing is silently half-done, and nothing is lost.
 * The name's required and BR-1 length checks run before the first request.
 */
export function* onSaveLexiconDraft() {
  yield* takeLatest(Actions.SAVE_LEXICON_DRAFT, function* () {
    try {
      // These two guards used to sit ABOVE the try, so the `finally` never saw
      // them. `!editing || !diff` in particular dispatched NOTHING, and the
      // reducer had already set `isMutating: true` on the action that got here
      // — so a Save that arrived after the drawer state was gone left every
      // mutation control on the page disabled until reload, the CSV dialog's
      // Import button included.
      const editing = yield* select(selectEditing);
      const diff = yield* select(selectDraftDiff);
      if (!editing || !diff) {
        return;
      }
      if (!diff.name) {
        yield* put(
          Actions.SET_DRAFT_NAME_ERROR({
            message: 'A lexicon name is required.',
          })
        );
        return;
      }
      if (yield* rejectNameTooLong(diff.name)) {
        return;
      }
      // INSIDE the try on purpose: resolving the registry schema or the auth
      // context can fail, and when this sat outside it that failure cleared
      // nothing and reported nothing — the page's mutation controls simply
      // stopped working until reload.
      const { exec, config, auditContext } = yield* call(getSagaContext);
      if (diff.isCreate) {
        const taken = yield* call(isLexiconNameTaken, exec, config, diff.name);
        if (taken) {
          yield* put(
            Actions.SET_DRAFT_NAME_ERROR({
              message: 'A lexicon with this name already exists.',
            })
          );
          return;
        }
        const created = yield* call(
          performAuditedLexiconCreate,
          exec,
          config,
          auditContext,
          diff.name,
          diff.added
        );
        yield* put(Actions.LEXICON_MUTATION_SUCCESS({ lexicon: created }));
        yield* put(Actions.CLOSE_LEXICON_DRAWER());
        yield* put(Actions.FETCH_LEXICONS());
        return;
      }

      const lexiconId = editing.lexiconId as string;
      let latest: Lexicon | undefined;
      // BR-6 — the drawer's edits are based on the revision read WHEN IT
      // OPENED. The non-commutative steps below (rename, remove) are guarded
      // against this value, advanced only by this save's own writes; guarding
      // each step against a fresh fetch made a concurrent admin's change
      // silently last-write-wins and left the conflict banner unreachable
      // (review-gate finding 3). Appends stay auto-retrying — they are
      // commutative by design (Q6 / U5-FR13) — but they advance the working
      // revision so a following remove is not guarded against a value the
      // append itself already superseded.
      let workingRevision = editing.baseRevision;

      if (diff.nameChanged) {
        const taken = yield* call(
          isLexiconNameTaken,
          exec,
          config,
          diff.name,
          lexiconId
        );
        if (taken) {
          yield* put(
            Actions.SET_DRAFT_NAME_ERROR({
              message: 'A lexicon with this name already exists.',
            })
          );
          return;
        }
        const current = yield* call(fetchLexiconById, exec, config, lexiconId);
        latest = yield* call(runGuardedMutation, {
          exec,
          config,
          auditContext,
          kind: 'lexicon.rename' as const,
          current,
          ...(workingRevision !== undefined
            ? { baseRevision: workingRevision }
            : {}),
          detail: { newName: diff.name },
          before: { name: current.name },
          after: { name: diff.name },
          mutate: (c: Lexicon) => renameLexicon(c, diff.name),
        });
        workingRevision = latest.revision;
      }

      if (diff.added.length > 0) {
        latest = yield* call(
          addEntriesWithRetry,
          exec,
          config,
          auditContext,
          lexiconId,
          diff.added
        );
        workingRevision = latest.revision;
      }

      // BETWEEN add and remove, and guarded like remove rather than retried like
      // add. An update is NOT commutative — two admins editing the same entry
      // must conflict rather than silently take the later write — so it is held
      // to `workingRevision` and advances it for the remove below.
      if (diff.updated.length > 0) {
        const current = yield* call(fetchLexiconById, exec, config, lexiconId);
        const updating = new Set(diff.updated.map((e) => e.entryId));
        const before = current.entries.filter((e) => updating.has(e.entryId));
        const beforeSample = boundEntrySample(before);
        const afterSample = boundEntrySample(diff.updated);
        latest = yield* call(runGuardedMutation, {
          exec,
          config,
          auditContext,
          kind: 'lexicon.entries.update' as const,
          current,
          ...(workingRevision !== undefined
            ? { baseRevision: workingRevision }
            : {}),
          detail: { entryCount: diff.updated.length },
          // Both sides, because an edit is the one mutation where neither half
          // is recoverable from the other: an add can be read off `after` and a
          // removal off `before`, but "changed X to Y" needs X and Y.
          before: {
            entryCount: current.entries.length,
            entries: beforeSample.entries,
            ...(beforeSample.entriesTruncated !== undefined
              ? { entriesTruncated: beforeSample.entriesTruncated }
              : {}),
          },
          after: {
            entryCount: current.entries.length,
            entries: afterSample.entries,
            ...(afterSample.entriesTruncated !== undefined
              ? { entriesTruncated: afterSample.entriesTruncated }
              : {}),
          },
          mutate: (c: Lexicon) => updateEntries(c, diff.updated),
        });
        workingRevision = latest.revision;
      }

      // SOFT delete. The entry stays in the array, tombstoned, so the drawer
      // can hand it back — the per-row delete control is unguarded and one
      // stray click must not be final. Audited as a REMOVE, not as the content
      // change it technically is: "changed a term" is the wrong thing to find
      // in a disclosure record when a term was taken out.
      if (diff.deletedEntryIds.length > 0) {
        const current = yield* call(fetchLexiconById, exec, config, lexiconId);
        const deleting = new Set(diff.deletedEntryIds);
        const removed = current.entries.filter((e) => deleting.has(e.entryId));
        const sample = boundEntrySample(removed);
        latest = yield* call(runGuardedMutation, {
          exec,
          config,
          auditContext,
          kind: 'lexicon.entries.remove' as const,
          current,
          ...(workingRevision !== undefined
            ? { baseRevision: workingRevision }
            : {}),
          detail: { entryCount: removed.length },
          before: {
            entryCount: liveEntries(current.entries).length,
            entries: sample.entries,
            ...(sample.entriesTruncated !== undefined
              ? { entriesTruncated: sample.entriesTruncated }
              : {}),
          },
          after: {
            entryCount: liveEntries(current.entries).length - removed.length,
          },
          mutate: (c: Lexicon) => removeEntries(c, diff.deletedEntryIds),
        });
        workingRevision = latest.revision;
      }

      // HARD delete, and the only step that shrinks the array. Reached only
      // through the drawer's Purge confirmation, because nothing gets it back.
      if (diff.purgedEntryIds.length > 0) {
        const current = yield* call(fetchLexiconById, exec, config, lexiconId);
        const purging = new Set(diff.purgedEntryIds);
        const purged = current.entries.filter((e) => purging.has(e.entryId));
        const sample = boundEntrySample(purged);
        latest = yield* call(runGuardedMutation, {
          exec,
          config,
          auditContext,
          kind: 'lexicon.entries.remove' as const,
          current,
          ...(workingRevision !== undefined
            ? { baseRevision: workingRevision }
            : {}),
          detail: { entryCount: purged.length },
          before: {
            entryCount: current.entries.length,
            entries: sample.entries,
            ...(sample.entriesTruncated !== undefined
              ? { entriesTruncated: sample.entriesTruncated }
              : {}),
          },
          after: { entryCount: current.entries.length - purged.length },
          mutate: (c: Lexicon) => purgeEntries(c, diff.purgedEntryIds),
        });
      }

      yield* put(
        Actions.LEXICON_MUTATION_SUCCESS(latest ? { lexicon: latest } : {})
      );
      yield* put(Actions.CLOSE_LEXICON_DRAWER());
      yield* put(Actions.FETCH_LEXICONS());
    } catch (error) {
      yield* reportMutationError(error);
    } finally {
      yield* settleMutation();
    }
  });
}

/**
 * Watchers to fork from `initMainPage` (Step 6 of the code-generation plan).
 *
 * Exported as a list so the route saga wires one thing rather than seven, and so
 * adding a watcher cannot be forgotten at the call site.
 */

/**
 * Fills in the entries of the lexicon the drawer just opened.
 *
 * The list holds SUMMARIES — no entries — so opening a lexicon to edit it needs
 * a read. `OPEN_LEXICON_DRAWER` seeds the name and the base revision from the
 * summary it already has and leaves `entries` empty with `isFetching` set; this
 * fills the rest in.
 *
 * It dispatches the same success action the conflict banner's Reload uses,
 * because the two do the same thing: replace the persisted entries, keep the
 * unsaved `draft:` ones, move the baseline. On open there are no unsaved ones,
 * so the shared case does the right thing for both.
 *
 * A failure reports FETCH_LEXICONS_FAILURE and leaves the drawer open with an
 * empty list rather than closing it. Closing a drawer someone just opened, on a
 * read they did not know was happening, is the worse answer.
 */
export function* onOpenLexiconDrawer() {
  yield* takeLatest(Actions.OPEN_LEXICON_DRAWER, function* ({ payload }) {
    if (!payload.lexiconId) {
      return;
    }
    const { exec, config } = yield* call(getSagaContext);
    try {
      const lexicon = yield* call(
        fetchLexiconById,
        exec,
        config,
        payload.lexiconId
      );
      yield* put(Actions.RELOAD_EDITING_LEXICON_SUCCESS({ lexicon }));
    } catch (error) {
      yield* put(Actions.FETCH_LEXICONS_FAILURE({ message: messageOf(error) }));
    }
  });
}

/** The conflict banner's Reload: take the other actor's version of the entries. */
export function* onReloadEditingLexicon() {
  yield* takeLatest(Actions.RELOAD_EDITING_LEXICON, function* () {
    const editing = yield* select(selectEditing);
    if (!editing?.lexiconId) {
      return;
    }
    const { exec, config } = yield* call(getSagaContext);
    try {
      const lexicon = yield* call(
        fetchLexiconById,
        exec,
        config,
        editing.lexiconId
      );
      yield* put(Actions.RELOAD_EDITING_LEXICON_SUCCESS({ lexicon }));
    } catch (error) {
      yield* put(Actions.FETCH_LEXICONS_FAILURE({ message: messageOf(error) }));
    }
  });
}

/**
 * Writes one lexicon out as CSV.
 *
 * A saga because the row only has a summary: the entries the file is made of
 * have to be read first, and so are the redaction codes — the file names each
 * entry's code as well as identifying it, and a name is something an author can
 * read and retype where an id is not.
 *
 * `ensureRedactionCodesLoaded` rather than a bare select: export is reachable
 * from the lexicon table's row menu, and while the Lexicons page does fetch all
 * codes on mount, a saga that silently wrote blank names when it happened to
 * run first would be a bug nobody could reproduce. It returns `{}` on failure
 * or timeout, which costs the two name columns and nothing else.
 */
export function* onExportLexicon() {
  yield* takeLatest(Actions.EXPORT_LEXICON, function* ({ payload }) {
    const { exec, config } = yield* call(getSagaContext);
    try {
      const lexicon = yield* call(
        fetchLexiconById,
        exec,
        config,
        payload.lexiconId
      );
      const codes = yield* call(ensureRedactionCodesLoaded);
      downloadBlob(
        new Blob([lexiconToCsv(lexicon, Object.values(codes))], {
          type: 'text/csv;charset=utf-8;',
        }),
        lexiconCsvFilename(lexicon)
      );
    } catch (error) {
      yield* put(
        Actions.LEXICON_MUTATION_FAILURE({ message: messageOf(error) })
      );
    }
  });
}

export const lexiconAdministrationSagas = [
  probeLexiconAvailability,
  onFetchLexicons,
  onFetchDeletedLexicons,
  onOpenLexiconDrawer,
  onReloadEditingLexicon,
  onExportLexicon,
  onSaveLexiconDraft,
  onCreateLexicon,
  onRenameLexicon,
  onAddLexiconEntries,
  onRemoveLexiconEntries,
  onSetLexiconFlags,
  onDeleteLexicon,
  onRestoreLexicon,
  onPurgeLexicon,
];
